import type { StreamSettings, VoiceSignal } from "@atrium/shared";
import { bitrateFor } from "./stream";

/**
 * WebRTC peer connections for one voice room, following the "perfect
 * negotiation" pattern so either side can renegotiate (screen share starting
 * and stopping, a listener becoming a speaker) without glare breaking it.
 *
 * Track layout per connection, kept deterministic so each side can tell a
 * microphone from a screen without extra signalling:
 *  - The first audio transceiver is the microphone slot. Whoever creates the
 *    connection adds it first (as `recvonly` if they have no microphone yet);
 *    the answering side attaches its microphone to that same transceiver.
 *  - Everything else is the screen: video plus optional system audio, added
 *    only once the connection has negotiated, and only to peers who asked to
 *    watch.
 *
 * Only the side with the lower socket id creates a connection; the other side
 * creates it when the first offer arrives. Signals from one peer are processed
 * strictly in order.
 */

export interface MeshOptions {
  selfId: string;
  iceServers: RTCIceServer[];
  send: (to: string, data: VoiceSignal) => void;
  /** Where hidden <audio> elements for remote microphones live. */
  container: HTMLElement | null;
  outputDeviceId: string | null;
  /** Whether a peer's microphone should be silent locally (deafened, or server-muted). */
  isSilenced: (id: string) => boolean;
  onScreen: (id: string, stream: MediaStream | null) => void;
  onMicStream: (id: string, stream: MediaStream) => void;
}

interface Peer {
  id: string;
  pc: RTCPeerConnection;
  polite: boolean;
  makingOffer: boolean;
  ignoreOffer: boolean;
  /** Has completed at least one offer/answer exchange. */
  negotiated: boolean;
  queue: Promise<void>;
  audio: HTMLAudioElement;
  micSender: RTCRtpSender | null;
  screenSenders: RTCRtpSender[];
  screenOf: MediaStream | null;
  remoteScreen: MediaStream | null;
  restarts: number;
}

const firstAudioTransceiver = (pc: RTCPeerConnection) => pc.getTransceivers().find((t) => t.receiver.track?.kind === "audio") ?? null;

export class Mesh {
  private peers = new Map<string, Peer>();
  private mic: MediaStream | null = null;
  private screen: MediaStream | null = null;
  private screenSettings: Pick<StreamSettings, "resolution" | "fps"> = { resolution: "1080", fps: 30 };
  /** Peers who asked to receive our screen. */
  private screenWanted = new Set<string>();
  private closed = false;

  constructor(private o: MeshOptions) {}

  get ids() {
    return [...this.peers.keys()];
  }

  /** Connect to exactly these peers: close the rest, open missing ones we're responsible for. */
  reconcile(desired: Set<string>) {
    if (this.closed) return;
    for (const id of [...this.peers.keys()]) if (!desired.has(id)) this.close(id);
    for (const id of desired) if (!this.peers.has(id) && id !== this.o.selfId && this.o.selfId < id) this.create(id, true);
  }

  private create(id: string, initiator: boolean): Peer {
    this.close(id);
    const pc = new RTCPeerConnection({ iceServers: this.o.iceServers });
    const audio = document.createElement("audio");
    audio.autoplay = true;
    audio.muted = this.o.isSilenced(id);
    const withSink = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    if (this.o.outputDeviceId && withSink.setSinkId) void withSink.setSinkId(this.o.outputDeviceId).catch(() => {});
    this.o.container?.appendChild(audio);
    const peer: Peer = {
      id,
      pc,
      polite: this.o.selfId > id,
      makingOffer: false,
      ignoreOffer: false,
      negotiated: false,
      queue: Promise.resolve(),
      audio,
      micSender: null,
      screenSenders: [],
      screenOf: null,
      remoteScreen: null,
      restarts: 0,
    };
    this.peers.set(id, peer);

    pc.onnegotiationneeded = async () => {
      try {
        peer.makingOffer = true;
        await pc.setLocalDescription();
        const d = pc.localDescription;
        if (d && (d.type === "offer" || d.type === "answer")) this.o.send(id, { type: d.type, sdp: d.sdp });
      } catch (e) {
        console.warn("voice: negotiation failed", e);
      } finally {
        peer.makingOffer = false;
      }
    };
    pc.onicecandidate = (e) => {
      if (e.candidate)
        this.o.send(id, { type: "candidate", candidate: { candidate: e.candidate.candidate, sdpMid: e.candidate.sdpMid, sdpMLineIndex: e.candidate.sdpMLineIndex } });
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" && peer.restarts < 3) {
        peer.restarts += 1;
        pc.restartIce();
      } else if (pc.connectionState === "connected") peer.restarts = 0;
    };
    pc.onsignalingstatechange = () => {
      if (pc.signalingState !== "stable" || !pc.remoteDescription) return;
      if (!peer.negotiated) {
        peer.negotiated = true;
        void this.attachMic(peer);
        this.syncScreen(peer);
      } else if (peer.screenSenders.length) void this.tuneScreen(peer);
    };
    pc.ontrack = (e) => {
      const isMic = e.transceiver === firstAudioTransceiver(pc);
      if (isMic) {
        const stream = new MediaStream([e.track]);
        audio.srcObject = stream;
        this.o.onMicStream(id, stream);
        return;
      }
      // Screen: video and its audio share one stream on the sender's side.
      let stream = e.streams[0] ?? peer.remoteScreen;
      if (!stream) stream = new MediaStream();
      if (!stream.getTracks().includes(e.track)) stream.addTrack(e.track);
      peer.remoteScreen = stream;
      this.o.onScreen(id, stream);
    };

    if (initiator) {
      // The microphone slot always comes first.
      const track = this.mic?.getAudioTracks()[0];
      if (track && this.mic) peer.micSender = pc.addTrack(track, this.mic);
      else pc.addTransceiver("audio", { direction: "recvonly" });
    }
    return peer;
  }

  close(id: string) {
    const p = this.peers.get(id);
    if (!p) return;
    this.peers.delete(id);
    p.pc.onnegotiationneeded = p.pc.onicecandidate = p.pc.ontrack = p.pc.onconnectionstatechange = p.pc.onsignalingstatechange = null;
    p.pc.close();
    p.audio.srcObject = null;
    p.audio.remove();
    if (p.remoteScreen) this.o.onScreen(id, null);
  }

  closeAll() {
    this.closed = true;
    for (const id of [...this.peers.keys()]) this.close(id);
    this.screenWanted.clear();
  }

  /** Signals are processed one at a time per peer, so a candidate never overtakes its description. */
  handleSignal(from: string, data: VoiceSignal) {
    if (this.closed || from === this.o.selfId) return;
    if (data.type === "watch") {
      if (data.on) this.screenWanted.add(from);
      else this.screenWanted.delete(from);
      const p = this.peers.get(from);
      if (p) this.syncScreen(p);
      // The per-viewer bitrate depends on how many are watching.
      this.retuneScreen();
      return;
    }
    let peer = this.peers.get(from);
    if (!peer) {
      if (data.type !== "offer") return;
      peer = this.create(from, false);
    }
    const p = peer;
    p.queue = p.queue.then(() => this.process(p, data)).catch((e) => console.warn("voice: signalling error", e));
  }

  private async process(peer: Peer, data: VoiceSignal): Promise<void> {
    const current = this.peers.get(peer.id);
    // Rebuilt since this was queued: hand it to the new connection.
    if (current !== peer) return current ? this.process(current, data) : undefined;
    const pc = peer.pc;
    if (data.type === "candidate") {
      try {
        await pc.addIceCandidate(data.candidate);
      } catch (e) {
        if (!peer.ignoreOffer) console.warn("voice: bad candidate", e);
      }
      return;
    }
    if (data.type !== "offer" && data.type !== "answer") return;
    const offerCollision = data.type === "offer" && (peer.makingOffer || pc.signalingState !== "stable");
    peer.ignoreOffer = !peer.polite && offerCollision;
    if (peer.ignoreOffer) return;
    if (data.type === "answer" && pc.signalingState !== "have-local-offer") return;
    try {
      // Polite side of a glare: drop our offer. (If ours is still being made, setRemoteDescription rolls back implicitly.)
      if (offerCollision && pc.signalingState === "have-local-offer") await pc.setLocalDescription({ type: "rollback" });
      await pc.setRemoteDescription({ type: data.type, sdp: data.sdp });
    } catch (e) {
      // An offer for a brand new session (the other side rebuilt its connection): start over.
      if (data.type !== "offer") throw e;
      const fresh = this.create(peer.id, false);
      await fresh.pc.setRemoteDescription({ type: "offer", sdp: data.sdp });
      await this.answer(fresh);
      return;
    }
    if (data.type === "offer") await this.answer(peer);
  }

  private async answer(peer: Peer) {
    await this.attachMic(peer);
    await peer.pc.setLocalDescription();
    const d = peer.pc.localDescription;
    if (d) this.o.send(peer.id, { type: "answer", sdp: d.sdp });
  }

  /** Put our microphone on the connection's microphone slot (or replace it). */
  private async attachMic(peer: Peer) {
    const track = this.mic?.getAudioTracks()[0];
    if (!track || !this.mic) return;
    if (peer.micSender) {
      if (peer.micSender.track !== track) await peer.micSender.replaceTrack(track).catch(() => {});
      return;
    }
    const t = firstAudioTransceiver(peer.pc);
    if (t && !t.sender.track && !peer.screenSenders.includes(t.sender)) {
      if (t.direction === "recvonly") t.direction = "sendrecv";
      else if (t.direction === "inactive") t.direction = "sendonly";
      (t.sender as RTCRtpSender & { setStreams?: (...s: MediaStream[]) => void }).setStreams?.(this.mic);
      await t.sender.replaceTrack(track).catch(() => {});
      peer.micSender = t.sender;
    } else if (!t) {
      peer.micSender = peer.pc.addTrack(track, this.mic);
    }
  }

  /** Set (or swap) the microphone: used when joining, changing device, or being invited on stage. */
  setMic(stream: MediaStream | null) {
    this.mic = stream;
    for (const p of this.peers.values()) if (p.negotiated || p.micSender) void this.attachMic(p);
  }

  setScreen(stream: MediaStream | null, settings?: Pick<StreamSettings, "resolution" | "fps">) {
    this.screen = stream;
    if (settings) this.screenSettings = settings;
    for (const p of this.peers.values()) this.syncScreen(p);
  }

  /**
   * Swap the stream's source (a new window or screen) without viewers losing it.
   * Tracks are replaced in place when the layout matches; gaining or losing
   * audio needs a renegotiation, so those peers get the new stream re-added.
   */
  replaceScreen(stream: MediaStream) {
    const old = this.screen;
    this.screen = stream;
    const layout = (kinds: (string | undefined)[]) => kinds.sort().join(",");
    for (const p of this.peers.values()) {
      if (!p.screenSenders.length || p.screenOf !== old || layout(p.screenSenders.map((s) => s.track?.kind)) !== layout(stream.getTracks().map((t) => t.kind))) {
        this.syncScreen(p);
        continue;
      }
      for (const sender of p.screenSenders) {
        const track = stream.getTracks().find((t) => t.kind === sender.track?.kind);
        if (track) void sender.replaceTrack(track).catch(() => {});
      }
      p.screenOf = stream;
    }
    this.retuneScreen();
  }

  /** New quality settings, or a different number of viewers. */
  retuneScreen(settings?: Pick<StreamSettings, "resolution" | "fps">) {
    if (settings) this.screenSettings = settings;
    for (const p of this.peers.values()) if (p.screenSenders.length) void this.tuneScreen(p);
  }

  /** Forget who asked to watch (we stopped sharing, so they'll ask again next time). */
  clearScreenWanted() {
    this.screenWanted.clear();
  }

  private syncScreen(peer: Peer) {
    if (this.peers.get(peer.id) !== peer || !peer.negotiated) return;
    const want = !!this.screen && this.screenWanted.has(peer.id);
    if (peer.screenSenders.length && (!want || peer.screenOf !== this.screen)) {
      for (const s of peer.screenSenders) {
        try {
          peer.pc.removeTrack(s);
        } catch {
          /* connection closing */
        }
      }
      peer.screenSenders = [];
      peer.screenOf = null;
    }
    if (want && this.screen && !peer.screenSenders.length) {
      const screen = this.screen;
      for (const t of screen.getTracks()) peer.screenSenders.push(peer.pc.addTrack(t, screen));
      peer.screenOf = screen;
      void this.tuneScreen(peer);
    }
  }

  /** Bitrate and frame rate ceilings to match the chosen quality, shared fairly between viewers. */
  private async tuneScreen(peer: Peer) {
    const viewers = Math.max(1, [...this.screenWanted].filter((id) => this.peers.has(id)).length);
    const { fps } = this.screenSettings;
    for (const s of peer.screenSenders) {
      if (s.track?.kind !== "video") continue;
      try {
        const params = s.getParameters();
        if (!params.encodings?.length) continue;
        params.encodings[0].maxBitrate = bitrateFor(this.screenSettings, viewers);
        params.encodings[0].maxFramerate = fps;
        await s.setParameters(params);
      } catch {
        /* not supported before negotiation in some browsers */
      }
      try {
        // Under pressure, keep text sharp at low frame rates and keep motion smooth at 60.
        const params = s.getParameters() as RTCRtpSendParameters & { degradationPreference?: string };
        params.degradationPreference = fps >= 60 ? "maintain-framerate" : "maintain-resolution";
        await s.setParameters(params);
      } catch {
        /* older browsers */
      }
    }
  }

  /** The viewer stopped watching: drop the stream locally straight away. */
  dropRemoteScreen(id: string) {
    const p = this.peers.get(id);
    if (p?.remoteScreen) {
      p.remoteScreen = null;
      this.o.onScreen(id, null);
    }
  }

  applySilence() {
    for (const p of this.peers.values()) p.audio.muted = this.o.isSilenced(p.id);
  }

  setOutput(deviceId: string | null) {
    this.o.outputDeviceId = deviceId;
    for (const p of this.peers.values()) {
      const a = p.audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      if (deviceId && a.setSinkId) void a.setSinkId(deviceId).catch(() => {});
    }
  }
}
